import { Component, EventEmitter, Input, Output } from '@angular/core';
import { animations } from './call-details-modal.animations';
import { Router } from '@angular/router';
import { ICallDetailModal } from 'src/app/interfaces/icall-detail-modal';
import { ITabDetail } from 'src/app/interfaces/itab-detail';
import { DeviceService } from 'src/app/services/general/device.service';
import { Observable, Subscription } from 'rxjs';
import { AmplitudeService } from 'src/app/services/external-tools/amplitude.service';
import { TranslationService } from 'src/app/services/translation.service';
import { TRANSLOCO_SCOPE, TranslocoService } from '@jsverse/transloco';
import { map } from 'rxjs/operators';
import { AppConstants } from 'src/app/app-constants';

@Component({
  selector: 'app-call-details-modal',
  templateUrl: './call-details-modal.component.html',
  styleUrls: ['./call-details-modal.component.scss'],
  animations: animations,
  standalone: false,
  providers: [
        {
          provide: TRANSLOCO_SCOPE,
          useValue: 'assignment',
        },
      ],
})
export class CallDetailsModalComponent {
  @Input() callDetails: ICallDetailModal | null = null;
  @Input() moduleAssignmentUuid: string | undefined = '';
  @Input() call_status: string | undefined = '';
  @Input() assignment_status: string | undefined = '';
  @Input() assignment_type: string | undefined = '';
  @Input() isMaxAttemptsReached: boolean = false;
  @Input() nonOperationalAssignmentStatus: string | null = null;
  @Output() closeModalEvent = new EventEmitter<void>();
  isClosing: boolean = false;
  sortedTabs: ITabDetail[] = [];
  activeTab: ITabDetail | null = null;
  callTitle: string = '';
  practiceType: string | null = '';
  callType: string = '';
  processedMarkdown: string = '';
  callUuid: string = '';
  isIpad: boolean = false;
  private deviceSubscription!: Subscription;
  AppConstants = AppConstants;

  constructor(
    private router: Router,
    private deviceService: DeviceService,
    private amplitudeService: AmplitudeService,
    private translationService: TranslationService,
    private translocoService: TranslocoService
  ) { }

  ngOnInit() {
    this.translationService.loadScopedTranslation('assignment');
    if (this.callDetails) {
      this.callTitle = this.callDetails?.callTitle;
      this.callUuid = this.callDetails?.callUuid;
      this.sortTabs();
      if (this.activeTab) {
        this.processedMarkdown = this.processMarkdown(this.activeTab.tabText);
      }
    }
    this.deviceSubscription = this.deviceService.isIpad$.subscribe((status) => {
      this.isIpad = status;
    });
  }

  ngOnDestroy(): void {
    if (this.deviceSubscription) {
      this.deviceSubscription.unsubscribe();
    }
  }

  closeModal() {
    this.isClosing = true;
  }

  onAnimationDone() {
    if (this.isClosing) {
      this.closeModalEvent.emit();
      this.isClosing = false;
    }
  }

  sortTabs() {
    if (this.callDetails) {
      this.sortedTabs = this.callDetails.callDetails
        .sort((a: any, b: any) => a.tabSequence - b.tabSequence)
        .map((tab: any) => ({
          ...tab,
          tabKey: typeof tab.tabTitle === 'string'
            ? tab.tabTitle.toUpperCase().replace(/\s+/g, '_')
            : tab.tabTitle
        }));
      if (this.sortedTabs.length > 0) {
        this.activeTab = this.sortedTabs[0];
      }
    }
  }

  setActiveTab(tab: any) {
    // Sending the Event of Clicking on Tab from Call Details Modal to Amplitude
    this.amplitudeService.trackEvent('calldetailsmodal_click_tab', {
      tab_name: tab.tabTitle,
    });

    this.activeTab = tab;
    if (this.activeTab) {
      this.processedMarkdown = this.processMarkdown(this.activeTab.tabText);
    }
  }

  navigateToStartCall(
    moduleAssignmentUuid: string | undefined,
    callUuid: string,
    practiceType: string | null,
    callType: string
  ) {
    // Sending the Event of Clicking on Launch Call from Call Details Modal to Amplitude
    this.amplitudeService.trackEvent('calldetailsmodal_click_launchcall', {
      call_status: this.call_status,
      assignment_status: this.assignment_status,
      assignment_type: this.assignment_type,
      call_type: callType,
    });
    const returnUrl = `/call?moduleAssignmentUuid=${moduleAssignmentUuid}&callUuid=${callUuid}&practiceType=${practiceType}&callType=${callType}`;
    localStorage.setItem('returnUrl', encodeURIComponent(returnUrl));

    this.router.navigate([`/call`], {
      queryParams: {
        moduleAssignmentUuid: moduleAssignmentUuid,
        callUuid: callUuid,
        practiceType: practiceType,
        callType: callType,
      },
    });
  }

  processMarkdown(markdownText: string): string {
    if (!markdownText) return '';

    markdownText = markdownText.replace(
      /@@/g,
      `<img src="assets/icons/checkmark.svg" alt="${this.translocoService.translate('CHECKMARK', {}, 'assignment')}">`
    );
    markdownText = markdownText.replace(/\n/g, '<br/>');
    markdownText = markdownText.replace(
      /\*\*(.+?)\*\*/g,
      '<strong>$1</strong>'
    );
    markdownText = markdownText.replace(/\*(.+?)\*/g, '<em>$1</em>');

    return markdownText;
  }

  translateTab$(tab: ITabDetail): Observable<string> {
    const fullKey = `assignment.${tab.tabKey}`;
    return this.translocoService.selectTranslate(fullKey).pipe(
      map(translated => {
        return translated === fullKey ? tab.tabTitle : translated;
      })
    );
  }

  /**
   * Returns true if the launch button should be hidden.
   * The button is hidden when the assignment status is CLOSED.
   */
  get shouldHideLaunchButton(): boolean {
    return this.nonOperationalAssignmentStatus?.toLowerCase() === AppConstants.CLOSED_ASSIGNMENT_STATUS;
  }

  /**
   * Returns true if the launch button should be disabled.
   * The button is disabled when the assignment status is STAGED or when max attempts are reached.
   */
  get shouldDisableLaunchButton(): boolean {
    return this.isMaxAttemptsReached || 
           this.nonOperationalAssignmentStatus?.toLowerCase() === AppConstants.STAGED_ASSIGNMENT_STATUS;
  }

  get visibleTabs(): ITabDetail[] {
    return this.sortedTabs.filter(
      tab => tab.tabText?.trim() && tab.tabTitle?.trim()
    );
  }


}
